import { createContext, useContext, useState } from 'react';
import { LocaleProvider, useLocale } from '../contexts/LocaleContext';

// EN / ES switch for the portals whose users choose their language (football
// operations, suppliers). The choice is remembered on this device.

const SetLocaleContext = createContext(null);

function readSaved(storageKey, fallback) {
  try {
    const v = localStorage.getItem(storageKey);
    return v === 'en' || v === 'es' ? v : fallback;
  } catch {
    return fallback;
  }
}

/** Spanish for a Spanish-language browser, otherwise English. */
export function browserLocale() {
  try {
    return String(navigator.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
  } catch {
    return 'en';
  }
}

/** A LocaleProvider the LanguageSwitch inside it can change. */
export function SwitchableLocaleProvider({ storageKey, defaultLocale, children }) {
  const [locale, setLocale] = useState(() => readSaved(storageKey, defaultLocale));
  function change(next) {
    setLocale(next);
    try { localStorage.setItem(storageKey, next); } catch { /* private mode — just this visit */ }
  }
  return (
    <SetLocaleContext.Provider value={change}>
      <LocaleProvider locale={locale}>{children}</LocaleProvider>
    </SetLocaleContext.Provider>
  );
}

/**
 * The switch. Pass locale/onChange, or leave them out inside a
 * SwitchableLocaleProvider. `tone` is 'dark' (on the navy bar) or 'light'.
 */
export function LanguageSwitch({ locale: localeProp, onChange: onChangeProp, tone = 'dark' }) {
  const setFromContext = useContext(SetLocaleContext);
  const { locale: contextLocale } = useLocale();
  const locale = localeProp || contextLocale;
  const onChange = onChangeProp || setFromContext;
  if (!onChange) return null;
  const dark = tone === 'dark';
  return (
    <div data-preview-allow className={`flex rounded-[8px] p-0.5 flex-shrink-0 ${dark ? 'bg-white/10' : 'bg-[#F3F4F6]'}`} role="radiogroup" aria-label="Language / Idioma">
      {[['es', 'ES', 'Español'], ['en', 'EN', 'English']].map(([key, label, name]) => (
        <button key={key} type="button" role="radio" aria-checked={locale === key} title={name}
          onClick={() => onChange(key)}
          className={`px-2.5 py-1 text-[11.5px] font-bold rounded-[6px] transition ${
            locale === key
              ? (dark ? 'bg-white text-navy' : 'bg-white text-navy shadow-sm')
              : (dark ? 'text-white/70 hover:text-white' : 'text-[#6B7280] hover:text-[#111827]')
          }`}>
          {label}
        </button>
      ))}
    </div>
  );
}
